import {
    HealthPlanLifeRecord,
    HealthPlanMovementRecord,
    HealthPlanParseResult,
} from '../healthPlanTypes';

import {
    extractTextFromPdf,
    normalizeUserCode,
    digitsOnly,
    toSignedCurrency,
    parseBradescoDate,
    parseBradescoCompetence,
    BRA_FULL_DATE_REGEX,
    BRA_CERTIFICATE_LINE_REGEX,
    BRA_RELATIONSHIP_LABELS,
    BRA_COMPETENCE_REGEX,
} from './parserUtils';

// Constants moved or re-imported. 
// Note: Some might be local to Bradesco if not shared.

// Duplicated local helpers if needed or import from utils if migrated
function formatRelationshipLabel(tokens: string[]): string {
    if (!tokens.length) return 'Titular';
    const formatted = tokens
        .map((token) => BRA_RELATIONSHIP_LABELS[token.toUpperCase()] ?? token)
        .join(' ')
        .trim();
    return formatted || 'Titular';
}

type BradescoCertificateInfo = {
    familyKey: string;
    suffix: string;
    digits: string;
};

function parseBradescoCertificateInfo(certificate: string): BradescoCertificateInfo {
    const normalized = certificate ?? '';
    const digits = normalized.replace(/\D/g, '');
    const [familyRaw = '', suffixRaw = ''] = normalized.split('/');
    const familyDigits = familyRaw.replace(/\D/g, '');
    const suffixDigits = suffixRaw.replace(/\D/g, '');

    if (familyDigits) {
        return {
            familyKey: familyDigits,
            suffix: suffixDigits ? suffixDigits.padStart(2, '0') : suffixDigits,
            digits,
        };
    }

    if (!digits) {
        return { familyKey: '', suffix: '', digits: '' };
    }

    if (digits.length <= 2) {
        return { familyKey: digits, suffix: '', digits };
    }

    return {
        familyKey: digits.slice(0, -2),
        suffix: digits.slice(-2),
        digits,
    };
}

export async function parseBradescoHealthPlanFiles(files: File[], password?: string, manualIof?: number): Promise<HealthPlanParseResult> {
    const pdfFile =
        files.find((file) => file.name.toLowerCase().includes('bradesco') && file.name.toLowerCase().endsWith('.pdf')) ??
        files.find((file) => (file.type ?? '').includes('pdf')) ??
        files.find((file) => file.name.toLowerCase().endsWith('.pdf')) ??
        null;

    if (!pdfFile) {
        throw new Error('Arquivo PDF do Bradesco é obrigatório.');
    }

    const textPages = await extractTextFromPdf(pdfFile, password);
    const livesByCertificate = new Map<string, HealthPlanLifeRecord>();
    const movements: HealthPlanMovementRecord[] = [];
    let lastLife: HealthPlanLifeRecord | null = null;
    let totalDebit = 0;
    let totalCredit = 0;
    let totalDebitCount = 0;
    let totalCreditCount = 0;
    let acTotal = 0;

    const addToTotals = (val: number | null) => {
        if (!val) return;
        if (val > 0) {
            totalDebit += val;
            totalDebitCount++;
        } else {
            totalCredit += val;
            totalCreditCount++;
        }
    };

    const pushMovement = (
        sourceLife: HealthPlanLifeRecord | null,
        planName: string,
        startDateToken: string | null,
        movementType: string,
        competenceToken: string | null,
        valueToken: string | null,
    ) => {
        if (!movementType) return;
        const movementValue = toSignedCurrency(valueToken);
        movements.push({
            movementType,
            beneficiaryName: sourceLife?.beneficiaryName ?? 'Beneficiário não identificado',
            beneficiaryCpf: sourceLife?.beneficiaryCpf ?? '',
            holderCpf: sourceLife?.holderCpf ?? '',
            relationship: sourceLife?.relationship ?? '',
            planName,
            inclusionDate: parseBradescoDate(startDateToken) ?? sourceLife?.inclusionDate ?? null,
            exclusionDate: null,
            movementCompetence: parseBradescoCompetence(competenceToken) ?? competenceToken ?? null,
            value: movementValue,
        });
    };

    const tryParseMovementLine = (line: string, sourceLife: HealthPlanLifeRecord | null) => {
        const trimmed = line.trim();
        if (!trimmed || !sourceLife) return;
        const tokens = trimmed.split(/\s+/).filter(Boolean);
        if (tokens.length < 5) return;
        const [planCandidate, dateToken, maybeMovement] = tokens;
        if (!BRA_FULL_DATE_REGEX.test(dateToken)) return;
        let idx = 2;
        let movementType: string | null = null;
        if (/^[A-Z]{2}$/.test(maybeMovement)) {
            movementType = maybeMovement;
            idx = 3;
        }
        if (!movementType) return;
        const competenceToken = tokens[idx] ?? null;
        const valueToken = tokens[idx + 1] ?? null;
        if (!competenceToken || !valueToken) return;
        pushMovement(sourceLife, planCandidate, dateToken, movementType, competenceToken, valueToken);
    };

    for (const pageText of textPages) {
        const lines = pageText.split('\n');
        for (const candidateLine of lines) {
            if (!candidateLine.trim()) continue;

            // Cleanup invisible characters
            const cleanLine = candidateLine.replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ').trim();

            if (cleanLine.includes('VICTOR') || cleanLine.includes('TNQQ')) {
            }
            if (candidateLine.includes('/00')) {

            }

            // Check for AC administrative fees
            const acMatch = cleanLine.match(/\bAC\s+(\d{2}\/\d{4})\s+([\d.,-]+)(\s+[\d.,-]+)?/);
            if (acMatch) {
                const valueToken = acMatch[2];
                const acValue = toSignedCurrency(valueToken);
                if (acValue) {
                    acTotal += acValue;
                    addToTotals(acValue);
                    continue;
                }
            }

            const match = candidateLine.match(BRA_CERTIFICATE_LINE_REGEX);
            if (candidateLine.includes('0002666')) {
            }
            if (match) {
                const certificate = match[1]?.trim() ?? '';
                const name = match[2]?.trim() ?? '';
                const birthDateToken = match[3]?.trim() ?? '';

                const isContinuation = lastLife && !name && certificate === lastLife.certificateCode;

                const rest = match[4]?.trim() ?? '';
                const restTokens = rest.split(/\s+/).filter(Boolean);

                if (!certificate || (!name && !isContinuation) || restTokens.length < 4) {
                    lastLife = null;
                    continue;
                }

                const firstDateIndex = restTokens.findIndex((token) => BRA_FULL_DATE_REGEX.test(token));
                if (firstDateIndex === -1) {
                    lastLife = null;
                    continue;
                }

                const planIndex = firstDateIndex - 1;
                if (planIndex < 0) {
                    lastLife = null;
                    continue;
                }
                const planName = restTokens[planIndex];
                const startDateToken = restTokens[firstDateIndex];
                if (!planName || !startDateToken) {
                    lastLife = null;
                    continue;
                }

                const afterStart = restTokens.slice(firstDateIndex + 1);
                if (!afterStart.length) {
                    lastLife = null;
                    continue;
                }

                let movementType: string | null = null;
                if (afterStart.length >= 3 && /^[A-Z]{2}$/.test(afterStart[0])) {
                    movementType = afterStart.shift() ?? null;
                }
                const competenceToken = afterStart.shift() ?? null;
                const premiumToken = afterStart.shift() ?? null;
                const partToken = afterStart.shift() ?? null;

                const premiumValue = toSignedCurrency(premiumToken);
                const partValue = toSignedCurrency(partToken) ?? 0;
                const relationshipTokens = restTokens.slice(2, planIndex).filter(t => !['MAS', 'FEM', 'CAS', 'SOLT', 'VIUV', 'DIV', 'OUT'].includes(t));
                const certificateInfo = parseBradescoCertificateInfo(certificate);
                const isHolder = !certificateInfo.suffix || certificateInfo.suffix === '00';
                const holderCpfValue = certificateInfo.familyKey || certificateInfo.digits || '';
                const relationshipLabel = formatRelationshipLabel(relationshipTokens);
                const relationship = relationshipLabel || (isHolder ? 'Titular' : 'Dependente');
                const userCodeDigits = certificateInfo.digits || certificate.replace(/\D/g, '');
                const familyIdentifier = holderCpfValue || certificateInfo.digits || '';

                const life: HealthPlanLifeRecord = {
                    beneficiaryName: name,
                    beneficiaryCpf: '',
                    holderCpf: '',
                    isPrimaryHolder: isHolder,
                    userCode: normalizeUserCode(userCodeDigits),
                    birthDate: parseBradescoDate(birthDateToken),
                    relationship,
                    planName,
                    inclusionDate: parseBradescoDate(startDateToken),
                    premiumTotal: premiumValue,
                    copayAmount: partValue,
                    totalCost: (premiumValue ?? 0) + (partValue ?? 0),
                    plans: [
                        {
                            planName,
                            premiumTotal: premiumValue,
                            copayAmount: partValue,
                        },
                    ],
                    copayEntries: [],
                    certificateCode: certificate,
                    familyIdentifier: familyIdentifier || null,

                };

                addToTotals(premiumValue);
                addToTotals(partValue);


                const certificateKeyRaw: string = certificateInfo.digits || certificate;
                const certificateKey: string = digitsOnly(certificateKeyRaw) || certificateKeyRaw;

                const existingLife: HealthPlanLifeRecord | undefined = certificateKey ? livesByCertificate.get(certificateKey) : undefined;

                if (existingLife) {
                    existingLife.premiumTotal = (existingLife.premiumTotal ?? 0) + (life.premiumTotal ?? 0);
                    existingLife.copayAmount = (existingLife.copayAmount ?? 0) + (life.copayAmount ?? 0);
                    existingLife.plans = [...(existingLife.plans ?? []), ...(life.plans ?? [])];
                    if (!existingLife.certificateCode)
                        existingLife.certificateCode = life.certificateCode ?? existingLife.certificateCode;
                    if (!existingLife.familyIdentifier && life.familyIdentifier) existingLife.familyIdentifier = life.familyIdentifier;
                    if (!existingLife.planName && life.planName) existingLife.planName = life.planName;
                    if (life.isPrimaryHolder && !existingLife.isPrimaryHolder) {
                        existingLife.beneficiaryName = life.beneficiaryName;
                        existingLife.userCode = life.userCode;
                        existingLife.birthDate = life.birthDate;
                        existingLife.relationship = life.relationship;
                        existingLife.planName = life.planName;
                        existingLife.inclusionDate = life.inclusionDate;
                        existingLife.isPrimaryHolder = true;
                    }
                    existingLife.totalCost = (existingLife.premiumTotal ?? 0) + (existingLife.copayAmount ?? 0);
                    lastLife = existingLife;
                } else {
                    const key = certificateKey || `${name}-${birthDateToken}-${planName}`;
                    livesByCertificate.set(key, life);
                    lastLife = life;
                }

                if (movementType && competenceToken && premiumToken) {
                    pushMovement(lastLife, planName, startDateToken, movementType, competenceToken, premiumToken);
                }

            } else if (lastLife) {
                // Tenta processar linha de continuação implícita (sem certificado no início)
                const tokens = candidateLine.trim().split(/\s+/);
                const firstDateIndex = tokens.findIndex((token) => BRA_FULL_DATE_REGEX.test(token));

                // Deve ter data e pelo menos competência + valor + copart (3 tokens após data)
                if (firstDateIndex === -1 || tokens.length < firstDateIndex + 1 + 3) {
                    continue;
                }

                const planName = tokens.slice(0, firstDateIndex).join(' ');

                // Tokens após a data de início
                const afterStart = tokens.slice(firstDateIndex + 1);

                let movementType: string | null = null;
                // Se o primeiro token parece um tipo (2 letras maiusculas) e não é competência
                // Ex: "CM 11/2025..." ou apenas "11/2025..."
                if (afterStart.length >= 4 && /^[A-Z]{2}$/.test(afterStart[0])) {
                    movementType = afterStart.shift() ?? null;
                }

                const competenceToken = afterStart.shift();
                const premiumToken = afterStart.shift();
                const partToken = afterStart.shift();

                // Validações básicas para garantir que é uma linha de movimento financeiro
                if (!competenceToken || !BRA_COMPETENCE_REGEX.test(competenceToken)) continue;

                const premiumValue = toSignedCurrency(premiumToken);
                const partValue = toSignedCurrency(partToken);

                if (premiumValue !== null) {
                    lastLife.premiumTotal = (lastLife.premiumTotal ?? 0) + premiumValue;
                    lastLife.copayAmount = (lastLife.copayAmount ?? 0) + (partValue ?? 0);
                    lastLife.totalCost = (lastLife.totalCost ?? 0) + premiumValue + (partValue ?? 0);

                    addToTotals(premiumValue);
                    addToTotals(partValue);

                    // Atualiza breakdown de planos
                    if (!lastLife.plans) lastLife.plans = [];

                    const existingPlan = lastLife.plans.find(p => p.planName === planName);
                    if (existingPlan) {
                        existingPlan.premiumTotal = (existingPlan.premiumTotal ?? 0) + premiumValue;
                        existingPlan.copayAmount = (existingPlan.copayAmount ?? 0) + (partValue ?? 0);
                    } else {
                        lastLife.plans.push({
                            planName: planName || lastLife.planName || '',
                            premiumTotal: premiumValue,
                            copayAmount: partValue ?? 0
                        });
                    }
                    continue;
                }
            }

            tryParseMovementLine(cleanLine, lastLife);
        }
    }

    if (acTotal !== 0) {
        const holders = Array.from(livesByCertificate.values()).filter(l => l.isPrimaryHolder);
        if (holders.length > 0) {
            const share = acTotal / holders.length;
            for (const holder of holders) {
                holder.premiumTotal = (holder.premiumTotal ?? 0) + share;
                holder.totalCost = (holder.totalCost ?? 0) + share;
                if (!holder.plans) holder.plans = [];
                holder.plans.push({
                    planName: 'Rateio Taxa Administrativa (AC)',
                    premiumTotal: share,
                    copayAmount: 0
                });
            }
        }
    }

    const lives: HealthPlanLifeRecord[] = Array.from(livesByCertificate.values());

    const premiumHealth = lives.reduce((acc, life) => acc + (life.premiumTotal ?? 0), 0);
    const activeLives = lives.length;
    const totalCost = premiumHealth + (manualIof ?? 0);

    return {
        lives,
        movements,
        copayEntries: [],
        totals: {
            premiumHealth: Number.isFinite(premiumHealth) ? premiumHealth : null,
            copayTotal: null,
            activeLives: Number.isFinite(activeLives) ? activeLives : null,
            totalCost: Number.isFinite(totalCost) ? totalCost : null,
            totalDebit: Number.isFinite(totalDebit) ? totalDebit : null,
            totalCredit: Number.isFinite(totalCredit) ? totalCredit : null,
            totalDebitCount: Number.isFinite(totalDebitCount) ? totalDebitCount : null,
            totalCreditCount: Number.isFinite(totalCreditCount) ? totalCreditCount : null,
            financialCost: Number.isFinite(manualIof) ? manualIof : null,
        },
    };
}
